import { CoordinateProjector } from '@trimble/geospatial';
import { ILogger } from '@trimble/logging';
import {
  createLonLatToSceneConverter,
  LonLatToSceneConverter,
} from '@trimble/web3d-providers';
import { Accessor, createSignal } from 'solid-js';

export interface CreateCadSceneProjectionParams {
  readonly csib: string;
  readonly geoDataFiles: Map<string, ArrayBuffer>;
  readonly logger: ILogger;
}

export interface CadSceneProjection {
  /** True once creation has settled, whether or not a converter exists. */
  readonly converterSettled: Accessor<boolean>;
  readonly projector: CoordinateProjector;
  ensureConverter(): Promise<LonLatToSceneConverter | undefined>;
  converter(): LonLatToSceneConverter | undefined;
  dispose(): void;
}

export function createCadSceneProjection(
  params: CreateCadSceneProjectionParams
): CadSceneProjection {
  let lonLatToScene: LonLatToSceneConverter | undefined;
  let converterPromise: Promise<LonLatToSceneConverter | undefined> | undefined;
  const [converterSettled, setConverterSettled] = createSignal(false);
  let disposed = false;

  // Identity until ready — mirror also checks converter() so coords aren't stuck.
  const projector: CoordinateProjector = (lon, lat) => {
    const scene = lonLatToScene?.lonLatToScene(lon, lat);
    return scene ? { x: scene.x, y: scene.y } : { x: lon, y: lat };
  };

  /**
   * Resolves once converter creation has settled, whether it succeeded or
   * failed. Returns undefined when creation failed; the failure is logged
   * here, so callers should treat undefined as "no CRS conversion available"
   * rather than an error needing separate handling. `converterSettled` reflects
   * that the attempt has settled, not that a converter exists — check the
   * resolved value (or `lonLatToScene`) to know whether one is available.
   */
  const ensureConverter = (): Promise<LonLatToSceneConverter | undefined> => {
    if (!converterPromise) {
      converterPromise = createLonLatToSceneConverter(
        params.csib,
        params.geoDataFiles
      )
        .then((converter) => {
          if (!disposed) {
            lonLatToScene = converter;
            setConverterSettled(true);
          }
          return converter;
        })
        .catch((error) => {
          params.logger.warn(
            'Failed to build lon/lat->scene converter',
            undefined,
            error instanceof Error ? error : new Error(String(error))
          );

          if (!disposed) {
            setConverterSettled(true);
          }
          return undefined;
        });
    }
    return converterPromise;
  };
  void ensureConverter();

  return {
    converterSettled,
    projector,
    ensureConverter,
    converter: () => lonLatToScene,
    dispose: () => {
      disposed = true;
    },
  };
}
